import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatCurrency, formatDate, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Landmark, Search, AlertTriangle, FileText, X } from "lucide-react";
import { toast } from "sonner";
import CustomerSearch from "@/components/CustomerSearch";
import Pagination from "@/components/Pagination";

const STATUS_ORDER = [
  "new_enquiry", "documents_pending", "documents_verified", "application_submitted",
  "under_scrutiny", "approved", "rejected",
  "permit_generated", "permit_received", "ready_for_sale",
  "sale_completed", "subsidy_completed", "expired", "cancelled",
];

const STATUS_TINT = {
  new_enquiry: "bg-[#121815] text-[#F8F9FA] border-[#3A4640]",
  documents_pending: "bg-yellow-500/15 text-yellow-200 border-yellow-500/30",
  documents_verified: "bg-yellow-500/10 text-yellow-300 border-yellow-500/30",
  application_submitted: "bg-blue-500/10 text-blue-300 border-blue-500/25",
  under_scrutiny: "bg-blue-100 text-blue-900 border-blue-500/25",
  approved: "bg-[#39FF14]/10 text-[#39FF14] border-[#39FF14]/25",
  rejected: "bg-red-500/15 text-red-300 border-red-500/25",
  permit_generated: "bg-emerald-100 text-emerald-900 border-emerald-200",
  permit_received: "bg-emerald-100 text-emerald-900 border-emerald-200",
  ready_for_sale: "bg-[#39FF14] text-[#0B0F0C] border-[#0f4c26]",
  sale_completed: "bg-[#1A1F1C] text-white border-[#0b1f11]",
  subsidy_completed: "bg-[#1A1F1C] text-white border-[#0b1f11]",
  expired: "bg-red-500/15 text-red-300 border-red-500/25",
  cancelled: "bg-[#1A1F1C] text-[#F8F9FA] border-[#3A4640]",
};

const emptyForm = {
  customer_id: "", financial_year: "", department: "", scheme: "",
  application_number: "", application_date: "", machine_category: "",
  expected_amount: 0, expected_percentage: 0, remarks: "",
};

export default function Subsidies() {
  const { user } = useAuth();
  const isDeskStaff = ["admin", "co_admin", "owner", "subsidy_desk_manager", "subsidy_desk_executive"].includes(user?.role);

  const [meta, setMeta] = useState({ counts: {}, labels: {}, departments: [], financial_years: [], schemes: [], machine_categories: [], expiring_soon: 0 });
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [pageInfo, setPageInfo] = useState({ page: 1, total: 0, total_pages: 0, page_size: 25 });
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [filters, setFilters] = useState({ status: "", financial_year: "", department: "", scheme: "", machine_category: "" });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [accessDenied, setAccessDenied] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      for (const [k, v] of Object.entries(filters)) if (v) params.set(k, v);
      params.set("page", String(page));
      params.set("page_size", String(pageSize));
      const [r, m] = await Promise.all([
        api.get(`/subsidies?${params.toString()}`),
        api.get("/subsidies/meta"),
      ]);
      const items = Array.isArray(r.data) ? r.data : (r.data?.items || []);
      setRows(items);
      setPageInfo({
        page: r.data?.page ?? 1,
        page_size: r.data?.page_size ?? pageSize,
        total: r.data?.total ?? items.length,
        total_pages: r.data?.total_pages ?? 1,
      });
      setMeta(m.data);
      setAccessDenied(false);
    } catch (err) {
      // Any 403 means this role is not permitted — render a friendly state
      // and never let the error bubble up to React's dev overlay.
      if (err?.response?.status === 403) {
        setAccessDenied(true);
      } else {
        toast.error(formatApiError(err?.response?.data?.detail) || "Could not load Subsidy Desk");
      }
    } finally { setLoading(false); }
  };
  // Reset to page 1 on filter/search change.
  useEffect(() => { setPage(1); /* eslint-disable-next-line */ }, [q, filters]);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q, filters, page]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer_id) return toast.error("Select a customer first");
    try {
      const payload = { ...form, expected_amount: parseFloat(form.expected_amount) || 0, expected_percentage: parseFloat(form.expected_percentage) || 0 };
      const { data } = await api.post("/subsidies", payload);
      toast.success(`Subsidy case ${data.case_no} opened`);
      setOpen(false); setForm(emptyForm); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed to create subsidy case"); }
  };

  const clearFilters = () => setFilters({ status: "", financial_year: "", department: "", scheme: "", machine_category: "" });

  const anyFilters = Object.values(filters).some(Boolean);
  const cards = useMemo(() => STATUS_ORDER.map((k) => ({ key: k, label: meta.labels?.[k] || k, count: meta.counts?.[k] || 0 })), [meta]);

  if (accessDenied) {
    return (
      <div className="p-8 lg:p-10 max-w-[1600px]" data-testid="subsidies-access-denied">
        <PageHeader title="Subsidy Desk" subtitle="Access restricted." testid="page-subsidies-header" />
        <div className="mt-6 rounded-xl border border-[#3A4640] bg-[#121815] p-8 text-center max-w-xl mx-auto">
          <AlertTriangle size={28} className="mx-auto text-[#FFB46B] mb-3" />
          <div className="text-base font-heading font-semibold text-[#F8F9FA]">Access restricted</div>
          <p className="text-sm text-[#8A8F98] mt-2">
            Your role doesn't have permission to view the Subsidy Desk.
            If you believe this is a mistake, contact your administrator.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 lg:p-10 max-w-[1600px]">
      <PageHeader
        title="Subsidy Desk"
        subtitle="Digital replacement of the physical subsidy desk — track every case from enquiry to sale completion."
        testid="page-subsidies-header"
        action={
          isDeskStaff ? (
            <Button data-testid="new-subsidy-btn" onClick={() => setOpen(true)} className="bg-[#39FF14] hover:bg-[#2ECC71]">
              <Plus size={14} className="mr-2" /> New Subsidy Case
            </Button>
          ) : null
        }
      />

      {/* Status cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-6" data-testid="subsidy-status-cards">
        {cards.map((c) => (
          <button
            key={c.key}
            onClick={() => setFilters((f) => ({ ...f, status: f.status === c.key ? "" : c.key }))}
            data-testid={`status-card-${c.key}`}
            className={`text-left px-3 py-2.5 rounded-md border transition-all ${filters.status === c.key ? "border-yellow-500/40 bg-yellow-500/10 shadow-sm" : "border-[#3A4640] bg-[#121815] hover:border-[#3A4640]"}`}
          >
            <div className="text-[10px] uppercase tracking-wider text-[#8A8F98] font-heading font-semibold leading-tight">{c.label}</div>
            <div className="font-heading text-2xl font-extrabold text-[#F8F9FA] mt-1">{c.count}</div>
          </button>
        ))}
      </div>

      {meta.expiring_soon > 0 && (
        <div className="mb-4 bg-yellow-500/10 border border-yellow-500/40 text-yellow-200 rounded p-3 flex items-center gap-2 text-sm" data-testid="expiring-banner">
          <AlertTriangle size={16} />
          <span><b>{meta.expiring_soon}</b> permit{meta.expiring_soon === 1 ? "" : "s"} expiring within 15 days.</span>
        </div>
      )}

      {/* Search + filters */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-[280px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8F98]" />
          <Input data-testid="subsidy-search" className="pl-9 bg-[#121815]" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search case number, application, permit, scheme…" />
        </div>
        <FilterSelect id="fy" value={filters.financial_year} onChange={(v) => setFilters({ ...filters, financial_year: v })} placeholder="Financial year" options={meta.financial_years} testid="filter-fy" />
        <FilterSelect id="dept" value={filters.department} onChange={(v) => setFilters({ ...filters, department: v })} placeholder="Department" options={meta.departments} testid="filter-dept" />
        <FilterSelect id="scheme" value={filters.scheme} onChange={(v) => setFilters({ ...filters, scheme: v })} placeholder="Scheme" options={meta.schemes} testid="filter-scheme" />
        <FilterSelect id="cat" value={filters.machine_category} onChange={(v) => setFilters({ ...filters, machine_category: v })} placeholder="Machine category" options={meta.machine_categories} testid="filter-category" />
        {(anyFilters || q) && <Button variant="ghost" size="sm" onClick={() => { clearFilters(); setQ(""); }} data-testid="clear-subsidy-filters"><X size={14} className="mr-1" /> Clear</Button>}
      </div>

      {/* Table */}
      <section className="bg-[#121815] border border-[#3A4640] rounded-md shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#0B0F0C] border-b border-[#3A4640] text-xs uppercase tracking-wider text-[#8A8F98] font-heading">
            <tr>
              <th className="text-left px-4 py-3">Case #</th>
              <th className="text-left px-4 py-3">Customer</th>
              <th className="text-left px-4 py-3">Scheme / Department</th>
              <th className="text-left px-4 py-3">Machine Category</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Permit</th>
              <th className="text-right px-4 py-3">Approved</th>
            </tr>
          </thead>
          <tbody data-testid="subsidy-tbody">
            {rows.length === 0 && <tr><td colSpan={7} className="text-center py-14 text-[#8A8F98]" data-testid="subsidy-empty">No subsidy cases match the current filters.</td></tr>}
            {rows.map((r) => {
              const daysToExpiry = r.permit_expiry ? Math.round((new Date(r.permit_expiry) - new Date()) / 86400000) : null;
              const warn = daysToExpiry !== null && daysToExpiry <= 15 && ["permit_generated", "permit_received", "ready_for_sale"].includes(r.status);
              return (
                <tr key={r.id} className="border-b border-[#3A4640] last:border-0 hover:bg-[#0B0F0C]" data-testid={`subsidy-row-${r.id}`}>
                  <td className="px-4 py-3 font-mono text-xs">
                    <Link to={`/subsidies/${r.id}`} className="hover:text-[#39FF14] font-semibold" data-testid={`open-subsidy-${r.id}`}>{r.case_no}</Link>
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-[#F8F9FA]">{r.customer?.name || r.customer?.organization_name || "—"}</div>
                    <div className="text-xs text-[#8A8F98] font-mono">{r.customer?.tzat_no}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-[#F8F9FA]">{r.scheme || "—"}</div>
                    <div className="text-xs text-[#8A8F98]">{r.department || ""}{r.financial_year ? ` · FY ${r.financial_year}` : ""}</div>
                  </td>
                  <td className="px-4 py-3 text-xs">{r.approved_machine_category || r.machine_category || "—"}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-heading px-2 py-1 rounded border ${STATUS_TINT[r.status] || ""}`}>{meta.labels?.[r.status] || r.status}</span>
                    {warn && (
                      <div className="text-[11px] mt-1 text-red-300 flex items-center gap-1"><AlertTriangle size={10} /> {daysToExpiry < 0 ? "Expired" : `Expires in ${daysToExpiry}d`}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs font-mono">{r.permit_number || "—"}</td>
                  <td className="px-4 py-3 text-right font-mono font-semibold text-[#39FF14]">{formatCurrency(r.approved_amount || r.expected_amount || 0)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <Pagination
          page={pageInfo.page}
          totalPages={pageInfo.total_pages}
          total={pageInfo.total}
          pageSize={pageInfo.page_size}
          onPageChange={setPage}
          loading={loading}
          testid="subsidies-pagination"
        />
      </section>

      {/* New subsidy case dialog */}
      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setForm(emptyForm); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">New Subsidy Case</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label>Customer *</Label>
              <CustomerSearch
                testid="subsidy-customer-search"
                value={form.customer_id}
                onChange={(c) => setForm({ ...form, customer_id: c?.id || "" })}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Financial Year</Label><Input value={form.financial_year} onChange={(e) => setForm({ ...form, financial_year: e.target.value })} placeholder="e.g. 2025-26" /></div>
              <div><Label>Department</Label><Input value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} placeholder="e.g. Agriculture Dept" /></div>
              <div><Label>Scheme</Label><Input value={form.scheme} onChange={(e) => setForm({ ...form, scheme: e.target.value })} /></div>
              <div><Label>Machine Category *</Label><Input value={form.machine_category} onChange={(e) => setForm({ ...form, machine_category: e.target.value })} placeholder="Power Tiller / Brush Cutter …" data-testid="subsidy-category-input" /></div>
              <div><Label>Application Number</Label><Input value={form.application_number} onChange={(e) => setForm({ ...form, application_number: e.target.value })} /></div>
              <div><Label>Application Date</Label><Input type="date" value={form.application_date} onChange={(e) => setForm({ ...form, application_date: e.target.value })} /></div>
              <div><Label>Expected Subsidy Amount ({"\u20B9"})</Label><Input type="number" value={form.expected_amount} onChange={(e) => setForm({ ...form, expected_amount: e.target.value })} /></div>
              <div><Label>Expected Subsidy %</Label><Input type="number" value={form.expected_percentage} onChange={(e) => setForm({ ...form, expected_percentage: e.target.value })} /></div>
            </div>
            <div><Label>Remarks</Label><Textarea rows={2} value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="subsidy-submit-btn" className="bg-[#39FF14] hover:bg-[#2ECC71]"><Landmark size={14} className="mr-2" /> Open case</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FilterSelect({ value, onChange, placeholder, options, testid }) {
  return (
    <Select value={value || "all"} onValueChange={(v) => onChange(v === "all" ? "" : v)}>
      <SelectTrigger data-testid={testid} className="w-[170px] bg-[#121815]"><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>
        <SelectItem value="all">{placeholder}</SelectItem>
        {(options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}
